import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { CommissionRuleDto } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { useCommissionRules, useDeleteRule, useSaveRule } from '../api';
import { RuleModal } from './rule-modal';

export function ruleScope(r: CommissionRuleDto) {
  if (r.barberId && r.serviceId) return `${r.barberName ?? 'Profissional'} + ${r.serviceName ?? 'serviço'}`;
  if (r.barberId) return `Profissional: ${r.barberName ?? '-'}`;
  if (r.serviceId) return `Serviço: ${r.serviceName ?? '-'}`;
  return 'Geral';
}
export const ruleValue = (r: Pick<CommissionRuleDto, 'type' | 'value'>) =>
  r.type === 'percent' ? `${(r.value / 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%` : money(r.value);

export function RulesTab() {
  const q = useCommissionRules();
  const save = useSaveRule();
  const del = useDeleteRule();
  const [editing, setEditing] = useState<CommissionRuleDto | 'new' | null>(null);

  const newButton = (
    <Button onClick={() => setEditing('new')}>
      <Plus className="size-4" aria-hidden /> Nova regra
    </Button>
  );

  async function toggle(r: CommissionRuleDto, active: boolean) {
    try {
      await save.mutateAsync({ id: r.id, body: { barberId: r.barberId, serviceId: r.serviceId, type: r.type, value: r.value, priority: r.priority, active } });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <div className="grid gap-4">
      <div className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">
        <p>
          A regra mais específica vence: profissional e serviço juntos, depois só profissional, depois só serviço e por último a regra geral. Em caso de empate, vale a maior prioridade.
        </p>
        <p className="mt-1">A comissão é calculada sobre o valor recebido do atendimento, já com descontos.</p>
      </div>
      <div className="flex justify-end">{newButton}</div>
      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState title="Nenhuma regra de comissão" description="Sem regras, nenhuma comissão é gerada nos recebimentos." action={newButton} />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {['Escopo', 'Tipo', 'Valor', 'Prioridade', 'Ativa'].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold text-muted-foreground">
                    {h}
                  </TableHead>
                ))}
                <TableHead className="text-right text-xs font-semibold text-muted-foreground">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(q.data ?? []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{ruleScope(r)}</TableCell>
                  <TableCell>{r.type === 'percent' ? 'Percentual' : 'Valor fixo'}</TableCell>
                  <TableCell className="tabular">{ruleValue(r)}</TableCell>
                  <TableCell className="tabular">{r.priority}</TableCell>
                  <TableCell>
                    <Switch checked={r.active} disabled={save.isPending} aria-label={`Regra ${ruleScope(r)} ativa`} onCheckedChange={(v) => toggle(r, v)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
                        Editar
                      </Button>
                      <ConfirmDialog
                        trigger={<Button size="sm" variant="ghost">Excluir</Button>}
                        title="Excluir esta regra?"
                        description={`A regra "${ruleScope(r)}" deixa de valer para novos recebimentos. Comissões já geradas não mudam.`}
                        confirmLabel="Excluir regra"
                        onConfirm={async () => {
                          try {
                            await del.mutateAsync(r.id);
                            toast.success('Regra excluída.');
                          } catch (e) {
                            toast.error(errorMessage(e));
                          }
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {editing && <RuleModal rule={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
